<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import CustomerFieldErrorMessageMixin from './mixins/customer_field_error_message';

import Icon from '~/components/icon.vue';

export default {
  components: {
    Icon
  },

  mixins: [CustomerFieldErrorMessageMixin],

  props: {
    name: {
      type: String,
      required: true
    },

    optional: {
      type: Boolean,
      required: false,
      default: false
    },

    type: {
      type: String,
      required: false,
      default: 'text'
    },

    focusedElement: {
      type: Boolean,
      required: false,
      default: false
    },

    label: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isFocus: false
    };
  },

  computed: {
    ...mapState([
      'noFocus'
    ]),

    ...mapState({
      input(state) {
        return state.inputs[this.name];
      },
      error(state) {
        return state.inputs[this.name].error;
      }
    }),

    value: {
      get() {
        return this.input.value;
      },

      set(value) {
        this.setValue({
          name: this.name,
          value
        });
      }
    },

    isText() {
      return this.type === 'text';
    },

    isPassword() {
      return this.type === 'password';
    },

    isTextArea() {
      return this.type === 'textarea';
    },

    optionalPlaceholder() {
      return this.optional ? this.t('pay.js.marketing.payment_information.optional').toLowerCase() : '';
    },

    optionalClass() {
      return this.optional ? 'c-form__input--optional' : '';
    },

    inputValid() {
      if (this.value) return !this.error && this.value.length > 0 && !this.isFocus;
      return false;
    },

    inputActive() {
      return this.input.value || this.error || this.isFocus ? true : false;
    },

    inputClass() {
      return {
        'c-form__input': true,
        'c-form__field--error': this.error,
        'c-form__field--inactive': !this.inputActive,
      };
    },

    labelFor() {
      return `payment_page_${this.name}`;
    },

    labelText() {
      const key = this.label || this.name;

      return this.t(`pay.js.marketing.payment_information.inputs.${key}`);
    },

  },

  mounted() {
    if (this.focusedElement && !this.noFocus) {
      this.$refs.input.focus();
    }
  },

  methods: {
    inputFocused() {
      this.isFocus = true;
    },

    inputFocusedOut() {
      this.isFocus = false;

      this.validateField(this.name);
    },

    ...mapActions([
      'validateField'
    ]),

    ...mapMutations({
      setValue: 'setInputValue'
    })
  }
};
</script>

<template>
  <div :class="inputClass">
    <div class="c-form__input-container">
      <label :for="labelFor">{{ labelText }}</label>

      <input
        v-if="isText"
        :id="labelFor"
        ref="input"
        v-model="value"
        :name="input.name"
        :class="optionalClass"
        :placeholder="optionalPlaceholder"
        type="text"
        @focus="inputFocused"
        @focusout="inputFocusedOut"
      >

      <input
        v-else-if="isPassword"
        :id="labelFor"
        v-model="value"
        :name="input.name"
        :class="optionalClass"
        :placeholder="optionalPlaceholder"
        type="password"
        @focus="inputFocused"
        @focusout="inputFocusedOut"
      >

      <textarea
        v-else-if="isTextArea"
        :id="labelFor"
        v-model="value"
        :name="input.name"
        :class="optionalClass"
        :placeholder="optionalPlaceholder"
        maxlength="255"
        rows="3"
        @focus="inputFocused"
        @focusout="inputFocusedOut"
      />

      <div
        v-if="inputValid"
        class="c-form__input__status"
      >
        <icon
          name="check"
          size="small"
        />
      </div>
    </div>

    <div class="c-form__feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>
